import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import TermsPage from "@/components/TermsPage";
import PrivacyPage from "@/components/PrivacyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Survey questions config ── */
const SURVEY_STEPS = [
  {
    key: "run_frequency",
    label: "Step 1 of 9",
    question: "How often do you run per week?",
    type: "options",
    options: ["I don't run yet", "1 - 2 times", "3 - 4 times", "5+ times"],
  },
  {
    key: "run_location",
    label: "Step 2 of 9",
    question: "Where do you typically run?",
    type: "options",
    options: ["Neighborhood streets", "Parks & trails", "Treadmill", "It varies"],
  },
  {
    key: "biggest_concern",
    label: "Step 3 of 9",
    question: "What's your biggest concern when running outdoors?",
    type: "options",
    options: [
      "Personal safety",
      "Traffic & road conditions",
      "Getting lost or unfamiliar areas",
      "Injury risk",
      "Weather",
      "None — I feel fine",
    ],
  },
  {
    key: "felt_unsafe",
    label: "Step 4 of 9",
    question: "Have you or someone you know ever felt unsafe while running?",
    type: "options",
    options: ["Yes", "No"],
  },
  {
    key: "current_tools",
    label: "Step 5 of 9",
    question: "What do you currently use to plan your running routes?",
    type: "options",
    options: ["Nothing — I just go", "Google Maps", "Strava", "AllTrails", "Nike Run Club", "Other"],
  },
  {
    key: "whats_missing",
    label: "Step 6 of 9",
    question: "What's missing from your current running experience?",
    type: "textarea",
    placeholder: "Tell us what frustrates you or what you wish existed...",
  },
  {
    key: "ai_interest",
    label: "Step 7 of 9",
    question: "Would you use an AI tool that generates safe, optimized routes based on your goals?",
    type: "options",
    options: ["Definitely", "Probably", "Not sure", "No"],
  },
  {
    key: "try_factor",
    label: "Step 8 of 9",
    question: "What would make you most likely to try a new running app?",
    type: "options",
    options: [
      "Safety features",
      "Route variety & discovery",
      "Community & social",
      "Training plans",
      "Free or affordable",
    ],
  },
  {
    key: "survey_email",
    label: "Step 9 of 9",
    question: "Want early access? Leave your email and we'll keep you posted.",
    type: "email",
    placeholder: "your@email.com",
  },
];

/* ═══════════════════════════════════ */
/*  Survey Modal Component            */
/* ═══════════════════════════════════ */
function SurveyModal({ onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const current = SURVEY_STEPS[step];
  const totalSteps = SURVEY_STEPS.length;

  const selectOption = (val) => {
    setAnswers((prev) => ({ ...prev, [current.key]: val }));
  };

  const isEmailStep = current?.type === "email";
  const emailValue = answers["survey_email"] || "";
  const isValidEmail = emailValue.includes("@") && emailValue.includes(".");
  const canProceed = current
    ? isEmailStep
      ? isValidEmail
      : current.type === "textarea"
        ? true
        : !!answers[current.key]
    : false;

  const handleNext = async () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      try {
        await axios.post(`${API}/survey`, answers);
      } catch (e) {
        console.error("Survey submit error", e);
      }
      setSubmitting(false);
      setDone(true);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  if (done) {
    return (
      <div className="survey-overlay" data-testid="survey-overlay" onClick={onClose}>
        <div className="survey-modal" data-testid="survey-modal" onClick={(e) => e.stopPropagation()}>
          <button className="survey-close" data-testid="survey-close-btn" onClick={onClose}>
            &times;
          </button>
          <div className="survey-done">
            <div className="survey-done-icon">&#10038;</div>
            <div className="survey-done-title">Thank you!</div>
            <div className="survey-done-sub">
              Your feedback helps us build something runners actually need. We'll keep you posted.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-overlay" data-testid="survey-overlay" onClick={onClose}>
      <div className="survey-modal" data-testid="survey-modal" onClick={(e) => e.stopPropagation()}>
        <button className="survey-close" data-testid="survey-close-btn" onClick={onClose}>
          &times;
        </button>

        {/* Progress bars */}
        <div className="survey-progress" data-testid="survey-progress">
          {SURVEY_STEPS.map((_, i) => (
            <div key={i} className={`survey-progress-bar ${i <= step ? "filled" : ""}`} />
          ))}
        </div>

        <div className="survey-step-label">{current.label}</div>
        <div className="survey-question" data-testid="survey-question">
          {current.question}
        </div>

        {current.type === "options" && (
          <div className="survey-options" data-testid="survey-options">
            {current.options.map((opt) => (
              <button
                key={opt}
                className={`survey-option ${answers[current.key] === opt ? "selected" : ""}`}
                data-testid={`survey-option-${opt.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                onClick={() => selectOption(opt)}
              >
                <span className="survey-option-dot" />
                {opt}
              </button>
            ))}
          </div>
        )}

        {current.type === "textarea" && (
          <textarea
            className="survey-textarea"
            data-testid="survey-textarea"
            placeholder={current.placeholder}
            value={answers[current.key] || ""}
            onChange={(e) => setAnswers((prev) => ({ ...prev, [current.key]: e.target.value }))}
          />
        )}

        {current.type === "email" && (
          <div style={{ marginBottom: "32px" }}>
            <input
              type="email"
              className="survey-email-input"
              data-testid="survey-email-input"
              placeholder={current.placeholder}
              value={answers[current.key] || ""}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [current.key]: e.target.value }))}
              autoComplete="off"
            />
            <p className="survey-email-hint">Required — so we can keep you in the loop</p>
          </div>
        )}

        <div className="survey-nav">
          {step > 0 ? (
            <button className="survey-back" data-testid="survey-back-btn" onClick={handleBack}>
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            className="survey-next"
            data-testid="survey-next-btn"
            disabled={!canProceed}
            onClick={handleNext}
          >
            {submitting ? "Sending..." : step === totalSteps - 1 ? "Submit" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════ */
/*  Main Landing Page                 */
/* ═══════════════════════════════════ */
function LandingPage() {
  const s1Ref = useRef(null);
  const hintRef = useRef(null);
  const curRef = useRef(null);
  const ringRef = useRef(null);

  const [currentWord, setCurrentWord] = useState(0);
  const [hintOpacity, setHintOpacity] = useState(1);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const prevWord = useRef(0);
  const wordRefs = [useRef(null), useRef(null), useRef(null)];

  // Fetch initial count + track pageview
  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((res) => setWaitlistCount(res.data.count))
      .catch(() => {});
    // Track page view
    axios.post(`${API}/analytics/pageview`, {
      page: window.location.pathname,
      referrer: document.referrer || "",
    }).catch(() => {});
  }, []);

  // Scroll handler
  const handleScroll = useCallback(() => {
    const s1 = s1Ref.current;
    if (!s1) return;
    const rect = s1.getBoundingClientRect();
    const scrolled = -rect.top;
    const total = s1.offsetHeight - window.innerHeight;
    const progress = Math.max(0, Math.min(1, scrolled / total));
    const idx = Math.min(Math.floor(progress * 3), 2);

    if (idx !== prevWord.current) {
      // Exit old word
      const oldEl = wordRefs[prevWord.current]?.current;
      if (oldEl) {
        oldEl.classList.remove("enter");
        oldEl.classList.add("exit");
        setTimeout(() => oldEl.classList.remove("exit"), 500);
      }
      // Enter new word
      const newEl = wordRefs[idx]?.current;
      if (newEl) {
        newEl.classList.remove("exit");
        void newEl.offsetWidth;
        newEl.classList.add("enter");
      }
      prevWord.current = idx;
      setCurrentWord(idx);
    }

    setHintOpacity(scrolled > 80 ? 0 : 1);
  }, []);

  useEffect(() => {
    // Initial word
    const el = wordRefs[0]?.current;
    if (el) el.classList.add("enter");

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  // Custom cursor
  useEffect(() => {
    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let rx = mx;
    let ry = my;
    let rafId;

    const onMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
    };
    document.addEventListener("mousemove", onMove);

    const loop = () => {
      rx += (mx - rx) * 0.13;
      ry += (my - ry) * 0.13;
      if (curRef.current) {
        curRef.current.style.left = mx + "px";
        curRef.current.style.top = my + "px";
      }
      if (ringRef.current) {
        ringRef.current.style.left = rx + "px";
        ringRef.current.style.top = ry + "px";
      }
      rafId = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      document.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Intersection observer for reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("show");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".ri").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Hover ring effect
  useEffect(() => {
    const interactives = document.querySelectorAll("button,input,a");
    const enter = () => {
      if (ringRef.current) {
        ringRef.current.style.width = "50px";
        ringRef.current.style.height = "50px";
        ringRef.current.style.borderColor = "rgba(0,212,216,.7)";
      }
    };
    const leave = () => {
      if (ringRef.current) {
        ringRef.current.style.width = "34px";
        ringRef.current.style.height = "34px";
        ringRef.current.style.borderColor = "rgba(0,212,216,.35)";
      }
    };
    interactives.forEach((el) => {
      el.addEventListener("mouseenter", enter);
      el.addEventListener("mouseleave", leave);
    });
    return () => {
      interactives.forEach((el) => {
        el.removeEventListener("mouseenter", enter);
        el.removeEventListener("mouseleave", leave);
      });
    };
  }, [submitted, surveyOpen]);

  // Submit waitlist
  const handleSubmit = async () => {
    if (!email.trim() || !email.includes("@")) {
      setEmailError(true);
      setTimeout(() => setEmailError(false), 1400);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { email: email.trim() });
      const countRes = await axios.get(`${API}/waitlist/count`);
      setWaitlistCount(countRes.data.count);
      setSubmitted(true);
    } catch (e) {
      console.error("Waitlist error", e);
    }
    setSubmitting(false);
  };

  const handleCopy = () => {
    const url = window.location.href.split("?")[0];
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url);
    }
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2200);
  };

  const words = ["SAFER", "SMARTER", "SECURE"];

  return (
    <>
      {/* Custom cursor */}
      <div className="pacely-cursor" ref={curRef} />
      <div className="pacely-ring" ref={ringRef} />

      {/* Toast */}
      <div className={`toast ${toastVisible ? "show" : ""}`} data-testid="copy-toast">
        Link copied!
      </div>

      {/* ═══ SECTION 1 ═══ */}
      <section className="section-scroll" ref={s1Ref} data-testid="section-scroll">
        <div className="sticky-inner">
          {/* Logo */}
          <div className="logo-wrap" data-testid="logo">
            <img src="/pacely-logo.png" alt="Pacely" />
          </div>

          <div className="headline">
            <span className="word-run">RUN</span>
            <div className="word-swap-wrap">
              {words.map((w, i) => (
                <span key={w} className="word-swap" ref={wordRefs[i]} data-testid={`word-${w.toLowerCase()}`}>
                  {w}
                </span>
              ))}
            </div>
          </div>

          <div className="dots" data-testid="scroll-dots">
            {words.map((_, i) => (
              <div key={i} className={`dot ${currentWord === i ? "active" : ""}`} />
            ))}
          </div>

          <div className="hint" ref={hintRef} style={{ opacity: hintOpacity }} data-testid="scroll-hint">
            <span>Scroll</span>
            <div className="hint-line" />
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2 ═══ */}
      <section className="section-content" data-testid="section-content">
        <div className="sep" />

        <p className="cap-label ri" style={{ animationDelay: ".08s" }}>
          Pacely — Coming Soon
        </p>

        <h2 className="cap-h ri" style={{ animationDelay: ".2s" }}>
          Something better
          <br />
          is coming.
        </h2>

        <p className="cap-teaser ri" style={{ animationDelay: ".3s" }}>
          AI-generated running routes, built around your goals.
        </p>

        <p className="cap-sub ri" style={{ animationDelay: ".38s" }}>
          Be the first to experience it.
        </p>

        <div className="counter-badge ri" style={{ animationDelay: ".46s" }} data-testid="counter-badge">
          <div className="counter-dot" />
          <span className="counter-text">
            <strong data-testid="counter-number">{waitlistCount}</strong> runners already on the list
          </span>
        </div>

        {!submitted ? (
          <div className={`form-wrap ${emailError ? "form-error" : ""}`} style={{ animationDelay: ".54s", animation: "fadeUp 1s cubic-bezier(.16,1,.3,1) .54s forwards", opacity: 0 }} data-testid="waitlist-form">
            <input
              type="email"
              className={`email-in ${emailError ? "error" : ""}`}
              data-testid="email-input"
              placeholder="your@email.com"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
            <button
              className="sub-btn"
              data-testid="join-waitlist-btn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Joining..." : "Join the waitlist"}
            </button>
          </div>
        ) : (
          <div className="success-msg" data-testid="success-message">
            &#10038; &nbsp;You're on the list. We'll be in touch.
          </div>
        )}

        {!submitted && (
          <p className="trust ri" style={{ animationDelay: ".68s" }}>
            Early access only. No spam.
          </p>
        )}

        <div className="share-row ri" style={{ animationDelay: ".78s" }}>
          <span className="share-label">Know a runner? Send them here</span>
          <button className="share-btn" data-testid="copy-link-btn" onClick={handleCopy}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            Copy link
          </button>
        </div>

        <div className="survey-row ri" style={{ animationDelay: ".88s" }}>
          <span className="survey-label-text">Running outdoors? Help us build it</span>
          <button
            className="survey-link"
            data-testid="open-survey-btn"
            onClick={() => setSurveyOpen(true)}
          >
            Take the 2-min runner survey
            <svg
              className="survey-arrow"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--teal)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>

        <div className="footer-bar">
          <p className="footer-note-inline">&copy; 2026 Pacely &nbsp;&middot;&nbsp; All rights reserved</p>
          <div className="footer-links">
            <Link to="/terms" className="footer-link" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy" className="footer-link" data-testid="footer-privacy-link">Privacy</Link>
          </div>
        </div>
      </section>

      {/* ═══ SURVEY MODAL ═══ */}
      {surveyOpen && <SurveyModal onClose={() => setSurveyOpen(false)} />}
    </>
  );
}

/* ═══════════════════════════════════ */
/*  Admin Page                        */
/* ═══════════════════════════════════ */
function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("analytics");
  const [loading, setLoading] = useState(true);

  const handleLogin = async () => {
    setAuthLoading(true);
    setAuthError(false);
    try {
      await axios.post(`${API}/admin/auth`, { password });
      setAuthed(true);
    } catch (e) {
      setAuthError(true);
    }
    setAuthLoading(false);
  };

  useEffect(() => {
    if (!authed) return;
    const fetch = async () => {
      try {
        const [wl, sv, an] = await Promise.all([
          axios.get(`${API}/waitlist`),
          axios.get(`${API}/survey`),
          axios.get(`${API}/analytics/stats`),
        ]);
        setWaitlist(wl.data);
        setSurveys(sv.data);
        setAnalytics(an.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetch();
  }, [authed]);

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const keys = Object.keys(data[0]).filter((k) => k !== "id");
    const header = keys.join(",");
    const rows = data.map((row) =>
      keys.map((k) => `"${String(row[k] || "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {!authed ? (
        <div className="admin-login-page" data-testid="admin-login">
          <div className="admin-login-card">
            <div className="admin-login-logo">
              <img src="/pacely-logo.png" alt="Pacely" style={{ height: "40px" }} />
            </div>
            <h2 className="admin-login-title">Admin Dashboard</h2>
            <p className="admin-login-sub">Enter password to continue</p>
            <input
              type="password"
              className={`admin-login-input ${authError ? "admin-login-error" : ""}`}
              data-testid="admin-password-input"
              placeholder="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setAuthError(false); }}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              autoFocus
            />
            {authError && <p className="admin-login-err-text" data-testid="admin-auth-error">Wrong password</p>}
            <button
              className="admin-login-btn"
              data-testid="admin-login-btn"
              onClick={handleLogin}
              disabled={authLoading || !password}
            >
              {authLoading ? "Checking..." : "Enter"}
            </button>
            <a href="/" className="admin-login-back">&larr; Back to site</a>
          </div>
        </div>
      ) : (
    <div className="admin-page" data-testid="admin-page">
      <div className="admin-header">
        <a href="/" className="admin-back" data-testid="admin-back-link">&larr; Back to site</a>
        <h1 className="admin-title">Pacely Admin</h1>
        <p className="admin-subtitle">Analytics, Waitlist &amp; Survey Dashboard</p>
      </div>

      <div className="admin-stats" data-testid="admin-stats">
        <div className="admin-stat-card">
          <div className="admin-stat-number" data-testid="admin-visitors-today">{analytics?.today?.unique || 0}</div>
          <div className="admin-stat-label">Visitors Today</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-number" data-testid="admin-visitors-week">{analytics?.week?.unique || 0}</div>
          <div className="admin-stat-label">This Week</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-number" data-testid="admin-waitlist-count">{waitlist.length}</div>
          <div className="admin-stat-label">Waitlist Signups</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-number" data-testid="admin-survey-count">{surveys.length}</div>
          <div className="admin-stat-label">Survey Responses</div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-number" data-testid="admin-conversion-rate">{analytics?.conversions?.rate || 0}%</div>
          <div className="admin-stat-label">Conversion Rate</div>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${tab === "analytics" ? "active" : ""}`}
          data-testid="admin-tab-analytics"
          onClick={() => setTab("analytics")}
        >
          Analytics
        </button>
        <button
          className={`admin-tab ${tab === "waitlist" ? "active" : ""}`}
          data-testid="admin-tab-waitlist"
          onClick={() => setTab("waitlist")}
        >
          Waitlist Emails
        </button>
        <button
          className={`admin-tab ${tab === "surveys" ? "active" : ""}`}
          data-testid="admin-tab-surveys"
          onClick={() => setTab("surveys")}
        >
          Survey Responses
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">Loading...</div>
      ) : tab === "analytics" ? (
        <div className="admin-section" data-testid="analytics-section">
          <div className="admin-section-header">
            <h2>Page Views — Last 14 Days</h2>
          </div>
          {analytics?.daily?.length > 0 ? (
            <div className="analytics-chart" data-testid="analytics-chart">
              {(() => {
                const maxViews = Math.max(...analytics.daily.map(d => d.views), 1);
                return analytics.daily.map((d) => (
                  <div key={d.date} className="analytics-bar-col">
                    <div className="analytics-bar-values">
                      <span className="analytics-bar-count">{d.views}</span>
                    </div>
                    <div className="analytics-bar-track">
                      <div
                        className="analytics-bar-fill"
                        style={{ height: `${Math.max((d.views / maxViews) * 100, 4)}%` }}
                      />
                    </div>
                    <span className="analytics-bar-label">{d.date.slice(5)}</span>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <p className="admin-empty">No pageview data yet — visits will appear here once tracked</p>
          )}

          <div className="analytics-grid">
            <div className="analytics-card">
              <h3>Traffic Summary</h3>
              <div className="analytics-kv">
                <span>Total Page Views</span><strong>{analytics?.total_views || 0}</strong>
              </div>
              <div className="analytics-kv">
                <span>Total Unique Visitors</span><strong>{analytics?.total_unique || 0}</strong>
              </div>
              <div className="analytics-kv">
                <span>Today Views</span><strong>{analytics?.today?.views || 0}</strong>
              </div>
              <div className="analytics-kv">
                <span>Today Unique</span><strong>{analytics?.today?.unique || 0}</strong>
              </div>
              <div className="analytics-kv">
                <span>This Week Views</span><strong>{analytics?.week?.views || 0}</strong>
              </div>
              <div className="analytics-kv">
                <span>This Month Views</span><strong>{analytics?.month?.views || 0}</strong>
              </div>
            </div>

            <div className="analytics-card">
              <h3>Conversions</h3>
              <div className="analytics-kv">
                <span>Waitlist Signups</span><strong>{analytics?.conversions?.waitlist || 0}</strong>
              </div>
              <div className="analytics-kv">
                <span>Survey Completions</span><strong>{analytics?.conversions?.survey || 0}</strong>
              </div>
              <div className="analytics-kv highlight">
                <span>Conversion Rate</span><strong>{analytics?.conversions?.rate || 0}%</strong>
              </div>
            </div>

            <div className="analytics-card">
              <h3>Top Referrers</h3>
              {analytics?.top_referrers?.length > 0 ? (
                analytics.top_referrers.map((r, i) => (
                  <div key={i} className="analytics-kv">
                    <span className="analytics-referrer">{r.referrer}</span><strong>{r.count}</strong>
                  </div>
                ))
              ) : (
                <p className="analytics-empty-note">No referrer data yet</p>
              )}
            </div>
          </div>
        </div>
      ) : tab === "waitlist" ? (
        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Waitlist ({waitlist.length})</h2>
            <button
              className="admin-export-btn"
              data-testid="export-waitlist-csv"
              onClick={() => exportCSV(waitlist, "pacely-waitlist.csv")}
            >
              Export CSV
            </button>
          </div>
          {waitlist.length === 0 ? (
            <p className="admin-empty">No signups yet</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="waitlist-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Email</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((entry, i) => (
                    <tr key={entry.id}>
                      <td>{i + 1}</td>
                      <td>{entry.email}</td>
                      <td>{new Date(entry.timestamp).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Survey Responses ({surveys.length})</h2>
            <button
              className="admin-export-btn"
              data-testid="export-survey-csv"
              onClick={() => exportCSV(surveys, "pacely-surveys.csv")}
            >
              Export CSV
            </button>
          </div>
          {surveys.length === 0 ? (
            <p className="admin-empty">No responses yet</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="survey-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Email</th>
                    <th>Frequency</th>
                    <th>Location</th>
                    <th>Concern</th>
                    <th>Felt Unsafe</th>
                    <th>Tools</th>
                    <th>What's Missing</th>
                    <th>AI Interest</th>
                    <th>Try Factor</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {surveys.map((s, i) => (
                    <tr key={s.id}>
                      <td>{i + 1}</td>
                      <td>{s.survey_email || "—"}</td>
                      <td>{s.run_frequency}</td>
                      <td>{s.run_location}</td>
                      <td>{s.biggest_concern}</td>
                      <td>{s.felt_unsafe}</td>
                      <td>{s.current_tools}</td>
                      <td className="admin-cell-long">{s.whats_missing || "—"}</td>
                      <td>{s.ai_interest}</td>
                      <td>{s.try_factor}</td>
                      <td>{new Date(s.timestamp).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════ */
/*  App Router                        */
/* ═══════════════════════════════════ */
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
